import type { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import type { UndoCommand, CommandContext } from "../types";

interface IndexedLineString {
  index: number;
  lineString: LineStringFeature;
}

/**
 * Command for moving (dragging) a marker to a new position.
 * Stores old/new position and the adjacent linestrings that changed.
 */
export class MoveMarkerCommand implements UndoCommand {
  readonly type = 'MOVE_MARKER';
  readonly routeKey: string;

  private markerIndex: number;
  private oldPosition: number[];
  private newPosition: number[];
  private oldLineStrings: IndexedLineString[];
  private newLineStrings: IndexedLineString[];

  constructor(data: {
    routeKey: string;
    markerIndex: number;
    oldPosition: number[];
    newPosition: number[];
    oldLineStrings: IndexedLineString[];
    newLineStrings: IndexedLineString[];
  }) {
    this.routeKey = data.routeKey;
    this.markerIndex = data.markerIndex;
    this.oldPosition = [...data.oldPosition];
    this.newPosition = [...data.newPosition];
    this.oldLineStrings = JSON.parse(JSON.stringify(data.oldLineStrings));
    this.newLineStrings = JSON.parse(JSON.stringify(data.newLineStrings));
  }

  undo(ctx: CommandContext): void {
    const route = ctx.state.routes[this.routeKey];
    if (!route) return;

    // Restore marker position
    const marker = route.markers[this.markerIndex];
    if (marker) {
      marker.markerGeometry.coordinates = [...this.oldPosition];
    }

    // Restore old linestrings
    for (const { index, lineString } of this.oldLineStrings) {
      if (index >= 0 && index < route.lineStrings.length) {
        route.lineStrings[index] = JSON.parse(JSON.stringify(lineString));
      }
    }

    route.updated_at = new Date();
  }

  redo(ctx: CommandContext): void {
    const route = ctx.state.routes[this.routeKey];
    if (!route) return;

    // Set new marker position
    const marker = route.markers[this.markerIndex];
    if (marker) {
      marker.markerGeometry.coordinates = [...this.newPosition];
    }

    // Set new linestrings
    for (const { index, lineString } of this.newLineStrings) {
      if (index >= 0 && index < route.lineStrings.length) {
        route.lineStrings[index] = JSON.parse(JSON.stringify(lineString));
      }
    }

    route.updated_at = new Date();
  }

  updateProvisionalLine(provisionalId: string, realLine: LineStringFeature): boolean {
    for (let i = 0; i < this.newLineStrings.length; i++) {
      const entry = this.newLineStrings[i];
      if (entry.lineString.lineStringProperties?.id === provisionalId) {
        this.newLineStrings[i] = {
          index: entry.index,
          lineString: JSON.parse(JSON.stringify(realLine))
        };
        return true;
      }
    }
    return false;
  }
}
